/**
 * usePwaUpdate — Stale-SW UX guard hook.
 *
 * Surfaces a banner when either:
 *   - reason='sw-update'      a new service worker is in `waiting` state
 *                             (the canonical vite-plugin-pwa prompt path).
 *   - reason='bundle-mismatch' the loaded entry-bundle hash differs from
 *                             the entry-bundle hash referenced by origin's
 *                             current /index.html (the user is on a stale
 *                             app shell and the SW hasn't yet reported a
 *                             waiting worker).
 *
 * Reload behaviour is reason-specific:
 *
 *   reason='sw-update' →
 *     await updateServiceWorker(true)
 *
 *   reason='bundle-mismatch' →
 *     await reg.update()                         (try to coax a waiting SW)
 *     if reg.waiting → updateServiceWorker(true) (canonical path)
 *     else           → reg.unregister()          (drop the stale controller)
 *                  → window.location.replace('/?amuster_force_reload=<ts>')
 *                                                (cache-busted hard recovery)
 *
 * The hard-recovery fallback is RESTRICTED to:
 *   • this hook file only (banner has no location.replace / location.reload)
 *   • reason==='bundle-mismatch' branch only
 *   • user-initiated reload() invocations only (never automatic)
 *
 * No `navigator.serviceWorker.register` is called from app code; the SW
 * is registered by useRegisterSW with vite-plugin-pwa's `injectRegister: null`
 * config — manual registration via the virtual module is the single path.
 *
 * Discovery of the loaded entry-bundle uses document.scripts first (the
 * live <script src=…> on the page), with performance.getEntriesByType
 * ('resource') as a fallback. import.meta.url is intentionally NOT used —
 * if vite splits this hook into a separate chunk later, import.meta.url
 * would point at the hook chunk, not the entry, and produce false positives.
 */

import { useRegisterSW } from 'virtual:pwa-register/react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type PwaUpdateReason = 'sw-update' | 'bundle-mismatch' | null;

/**
 * Matches Vite's hashed entry chunk: `/assets/index-<hash>.js`. The hash
 * portion can include any non-slash, non-quote character.
 */
const ENTRY_RE = /\/assets\/index-[^/"']+\.js/;

/**
 * Read the active loaded entry-bundle path from the live DOM. If the
 * <script> tag is no longer in document.scripts (e.g. it was already
 * removed after the module evaluated), fall back to the resource-timing
 * buffer. Returns null if neither yields a `/assets/index-*.js` URL —
 * the caller fails silent in that case.
 */
function getLoadedEntryScript(): string | null {
  for (const s of Array.from(document.scripts)) {
    if (ENTRY_RE.test(s.src)) {
      return new URL(s.src, location.origin).pathname;
    }
  }
  if (typeof performance?.getEntriesByType === 'function') {
    const entry = performance
      .getEntriesByType('resource')
      .map((r) => r.name)
      .find((n) => ENTRY_RE.test(n));
    if (entry) {
      return new URL(entry, location.origin).pathname;
    }
  }
  return null;
}

/**
 * Fetch origin's current /index.html via a cache-busted URL that is
 * unlikely to hit the SW precache. The query string makes the URL
 * unique per call (precache key won't match), `cache: 'no-store'` is
 * belt-and-braces against the HTTP cache, `Cache-Control: no-cache`
 * further discourages SW interception. Returns the first
 * `/assets/index-*.js` filename seen in the body, or null on parse /
 * network failure (fail silent).
 */
async function fetchOriginEntryScript(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?amuster_update_check=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const html = await res.text();
    const m = ENTRY_RE.exec(html);
    return m ? new URL(m[0], location.origin).pathname : null;
  } catch {
    return null;
  }
}

export interface UsePwaUpdate {
  /** True when the banner should render. Equivalent to `reason !== null`. */
  show: boolean;
  /** Why the banner is showing — drives the reload-path branching. */
  reason: PwaUpdateReason;
  /** User-initiated. Reason-specific reload behaviour. */
  reload: () => Promise<void>;
  /** User-initiated. Clears both the SW and bundle-mismatch flags. */
  dismiss: () => void;
}

export function usePwaUpdate(): UsePwaUpdate {
  // Stash the live SW registration captured at install-time so the
  // bundle-mismatch reload path can call reg.update() / reg.unregister()
  // without going back through navigator.serviceWorker.* directly.
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const [bundleMismatch, setBundleMismatch] = useState(false);

  const { needRefresh, updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
    },
    onRegisterError(e) {
      // Non-fatal — the page still works without a SW; banner just
      // never surfaces from the sw-update path.
      console.warn('[pwa] register error', e);
    },
  });

  // Explicit reason — NOT a derived OR of two booleans. reload()
  // switches on this value; tests assert against it.
  const reason: PwaUpdateReason = needRefresh[0]
    ? 'sw-update'
    : bundleMismatch
      ? 'bundle-mismatch'
      : null;

  const check = useCallback(async () => {
    const loaded = getLoadedEntryScript();
    const origin = await fetchOriginEntryScript();
    // Show mismatch ONLY when both endpoints are known AND differ.
    // Either being null = fail silent (no false positives).
    if (loaded != null && origin != null && loaded !== origin) {
      setBundleMismatch(true);
    }
  }, []);

  useEffect(() => {
    void check();
    const onVis = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [check]);

  const reload = useCallback(async () => {
    // ── Path A: waiting-SW (canonical) ─────────────────────────────
    if (reason === 'sw-update') {
      await updateServiceWorker(true);
      return;
    }

    // ── Path B: bundle-mismatch (with hard-recovery fallback) ──────
    if (reason === 'bundle-mismatch') {
      const reg = registrationRef.current;
      if (reg) {
        try {
          await reg.update();
        } catch (error) {
          console.warn('[pwa] registration.update() failed', error);
        }
        if (reg.waiting) {
          // A waiting SW now exists — funnel through the canonical
          // SKIP_WAITING + reload path.
          await updateServiceWorker(true);
          return;
        }
        try {
          await reg.unregister();
        } catch (error) {
          console.warn('[pwa] registration.unregister() failed', error);
        }
      }
      // Hard-recovery fallback: cache-busted navigation. The
      // amuster_force_reload query parameter ensures the new
      // navigation does not match any precache entry, and unregistering
      // the stale controller above prevents the OLD SW from intercepting
      // this navigation. The new page load will register the current SW
      // again via useRegisterSW on next mount.
      window.location.replace(`/?amuster_force_reload=${Date.now()}`);
      return;
    }

    // reason === null — defensive no-op. Banner shouldn't render in
    // this state; if reload() is somehow invoked from a stale closure,
    // do nothing rather than fall through.
  }, [reason, updateServiceWorker]);

  const dismiss = useCallback(() => {
    needRefresh[1](false);
    setBundleMismatch(false);
  }, [needRefresh]);

  return {
    show: reason !== null,
    reason,
    reload,
    dismiss,
  };
}
